import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Reveal } from "./Reveal";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

const localeFor = (lang) => (lang === "he" ? "he-IL" : lang === "ru" ? "ru-RU" : "en-US");
const ymd = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

// On-brand palette. Full literal class strings so Tailwind JIT keeps them.
const PALETTE = [
  { chip: "bg-[#BE923C]/15 text-[#9E7829]", dot: "bg-[#BE923C]", card: "border-[#BE923C]/30 bg-[#FBF7EE] hover:bg-[#f3e9d4]", tag: "text-[#9E7829]" },
  { chip: "bg-[#003063]/12 text-[#003063]", dot: "bg-[#003063]", card: "border-[#003063]/25 bg-[#003063]/5 hover:bg-[#003063]/10", tag: "text-[#003063]" },
  { chip: "bg-[#3F7E6F]/16 text-[#2F6355]", dot: "bg-[#3F7E6F]", card: "border-[#3F7E6F]/30 bg-[#3F7E6F]/8 hover:bg-[#3F7E6F]/14", tag: "text-[#2F6355]" },
  { chip: "bg-[#9B4D6A]/15 text-[#82405A]", dot: "bg-[#9B4D6A]", card: "border-[#9B4D6A]/30 bg-[#9B4D6A]/8 hover:bg-[#9B4D6A]/14", tag: "text-[#82405A]" },
  { chip: "bg-[#C05621]/15 text-[#9C4416]", dot: "bg-[#C05621]", card: "border-[#C05621]/30 bg-[#C05621]/8 hover:bg-[#C05621]/14", tag: "text-[#9C4416]" },
  { chip: "bg-[#4C5FD5]/14 text-[#3B4CC0]", dot: "bg-[#4C5FD5]", card: "border-[#4C5FD5]/30 bg-[#4C5FD5]/8 hover:bg-[#4C5FD5]/14", tag: "text-[#3B4CC0]" },
  { chip: "bg-[#7A8450]/18 text-[#5F683B]", dot: "bg-[#7A8450]", card: "border-[#7A8450]/35 bg-[#7A8450]/10 hover:bg-[#7A8450]/16", tag: "text-[#5F683B]" },
];
const NOTE_STYLE = { chip: "bg-[#64748B]/15 text-[#475569]", dot: "bg-[#64748B]", card: "border-[#64748B]/25 bg-[#64748B]/8", tag: "text-[#475569]" };

export function EventCalendar() {
  const { t, tl, lang } = useLang();
  const navigate = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null);

  const { data: events = [] } = useQuery({
    queryKey: ["events", "all"],
    queryFn: async () => (await api.get("/events")).data,
  });
  const { data: notes = [] } = useQuery({
    queryKey: ["calendar-notes"],
    queryFn: async () => (await api.get("/calendar-notes")).data,
  });

  // Assign each distinct event category a stable colour from the palette.
  const colorForCat = useMemo(() => {
    const cats = Array.from(new Set(events.map((e) => (e.category || "").trim() || "—"))).sort((a, b) => a.localeCompare(b));
    const map = {};
    cats.forEach((c, i) => { map[c] = PALETTE[i % PALETTE.length]; });
    return map;
  }, [events]);
  const catStyle = (cat) => colorForCat[(cat || "").trim() || "—"] || PALETTE[0];

  // Map date -> entries
  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!e.date) return;
      (map[e.date] ||= []).push({ kind: "event", id: e.id, title: tl(e, "title"), time: e.time, category: e.category || "" });
    });
    notes.forEach((n) => {
      if (!n.date) return;
      (map[n.date] ||= []).push({ kind: "note", id: n.id, title: tl(n, "title"), description: tl(n, "description") });
    });
    return map;
  }, [events, notes, lang]);

  // Legend: only categories actually present in events + a Notes swatch if any notes exist
  const legend = useMemo(() => {
    const present = Array.from(new Set(events.map((e) => (e.category || "").trim() || "—"))).sort((a, b) => a.localeCompare(b));
    return present.map((c) => ({ label: c === "—" ? t("calendar.general") : c, dot: catStyle(c).dot }));
  }, [events, colorForCat, lang]);

  const locale = localeFor(lang);
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString(locale, { month: "long", year: "numeric" });
  const weekdays = useMemo(() => Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 8, 1 + i).toLocaleDateString(locale, { weekday: "short" })), [locale]);

  const firstDay = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const shift = (delta) => setCursor((c) => {
    const nm = c.m + delta;
    return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
  });
  const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });

  const todayStr = ymd(today.getFullYear(), today.getMonth(), today.getDate());
  const selectedEntries = selected ? byDate[selected] || [] : [];

  return (
    <section data-testid="calendar-section" className="pb-24 sm:pb-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="calendar.eyebrow" as="span" /></span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight"><EditableText k="calendar.title" as="span" /></h2>
            </div>
            <div className="flex items-center gap-2" dir="ltr">
              <button data-testid="calendar-today" onClick={goToday}
                className="rounded-full border border-[#0F172A]/12 px-4 py-2 text-sm font-semibold text-[#0F172A] hover:border-[#BE923C] transition-colors">
                <EditableText k="calendar.today" as="span" />
              </button>
              <button data-testid="calendar-prev" onClick={() => shift(-1)} aria-label="Previous month"
                className="w-10 h-10 rounded-full border border-[#0F172A]/12 flex items-center justify-center hover:border-[#BE923C] transition-colors"><ChevronLeft size={18} /></button>
              <button data-testid="calendar-next" onClick={() => shift(1)} aria-label="Next month"
                className="w-10 h-10 rounded-full border border-[#0F172A]/12 flex items-center justify-center hover:border-[#BE923C] transition-colors"><ChevronRight size={18} /></button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-6 rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] overflow-hidden" dir="ltr">
            <div className="flex items-center justify-center py-4 border-b border-[#0F172A]/5">
              <p data-testid="calendar-month-label" className="font-serif text-xl text-[#0F172A] capitalize">{monthLabel}</p>
            </div>

            <div className="grid grid-cols-7 border-b border-[#0F172A]/5">
              {weekdays.map((w, i) => (
                <div key={i} className="py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">{w}</div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={`${cursor.y}-${cursor.m}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                className="grid grid-cols-7"
              >
                {cells.map((d, idx) => {
                  if (d === null) return <div key={idx} className="min-h-[92px] sm:min-h-[110px] border-b border-r border-[#0F172A]/5 bg-[#FAFAF8]" />;
                  const ds = ymd(cursor.y, cursor.m, d);
                  const entries = byDate[ds] || [];
                  const isToday = ds === todayStr;
                  const hasEntries = entries.length > 0;
                  return (
                    <button
                      key={idx}
                      type="button"
                      data-testid={`calendar-day-${ds}`}
                      onClick={() => hasEntries && setSelected(ds)}
                      className={`group min-h-[92px] sm:min-h-[110px] border-b border-r border-[#0F172A]/5 p-1.5 sm:p-2 text-left align-top transition-colors ${hasEntries ? "cursor-pointer hover:bg-[#FBF7EE]" : "cursor-default"}`}
                    >
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${isToday ? "bg-[#BE923C] text-white" : "text-[#475569]"}`}>{d}</span>
                      <div className="mt-1 space-y-1">
                        {entries.slice(0, 2).map((en, i) => {
                          const st = en.kind === "event" ? catStyle(en.category) : NOTE_STYLE;
                          return (
                            <div key={i} className={`flex items-center gap-1 truncate rounded px-1.5 py-0.5 text-[11px] leading-tight font-medium ${st.chip}`}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${st.dot}`} />
                              <span className="truncate">{en.title || (en.kind === "event" ? t("calendar.event") : t("calendar.note"))}</span>
                            </div>
                          );
                        })}
                        {entries.length > 2 && (
                          <div className="px-1.5 text-[11px] font-semibold text-[#94A3B8]">+{entries.length - 2} {t("calendar.more")}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>

          {(legend.length > 0 || notes.length > 0) && (
            <div data-testid="calendar-legend" className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#475569]" dir="ltr">
              {legend.map((l, i) => (
                <span key={i} className="inline-flex items-center gap-2"><span className={`w-3 h-3 rounded ${l.dot}`} /> {l.label}</span>
              ))}
              {notes.length > 0 && (
                <span className="inline-flex items-center gap-2"><span className={`w-3 h-3 rounded ${NOTE_STYLE.dot}`} /> <EditableText k="calendar.legend_note" as="span" /></span>
              )}
            </div>
          )}
        </Reveal>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="calendar-day-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {selected && new Date(selected + "T00:00:00").toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            {selectedEntries.length === 0 && <p className="text-[#475569]"><EditableText k="calendar.no_entries" as="span" /></p>}
            {selectedEntries.map((en, i) => {
              if (en.kind === "event") {
                const st = catStyle(en.category);
                return (
                  <button key={i} data-testid={`calendar-entry-event-${en.id}`}
                    onClick={() => { setSelected(null); navigate(`/events/${en.slug || en.id}`); }}
                    className={`w-full text-left rounded-2xl border p-4 transition-colors group ${st.card}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${st.tag}`}>
                        <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                        {en.category || t("calendar.event")}
                      </span>
                      <ArrowUpRight size={16} className={`${st.tag} opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </div>
                    <p className="mt-1 font-serif text-lg text-[#0F172A]">{en.title}</p>
                    {en.time && <p className="text-sm text-[#475569]">{en.time}</p>}
                    <p className={`mt-1 text-sm font-semibold ${st.tag}`}>{t("calendar.view_event")} →</p>
                  </button>
                );
              }
              return (
                <div key={i} data-testid="calendar-entry-note" className={`rounded-2xl border p-4 ${NOTE_STYLE.card}`}>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${NOTE_STYLE.tag}`}>
                    <span className={`w-2 h-2 rounded-full ${NOTE_STYLE.dot}`} />
                    <EditableText k="calendar.note" as="span" />
                  </span>
                  <p className="mt-1 font-serif text-lg text-[#0F172A]">{en.title}</p>
                  {en.description && <p className="mt-1 text-sm text-[#475569] whitespace-pre-line">{en.description}</p>}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
